import { useEffect, useMemo, useState } from "react";
import { CircleAlertIcon, EditIcon, MessageCircleIcon, PlusIcon, TrashIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmationDialog } from "@/components/custom/confirmation-dialog";
import { formsApi } from "@/lib/api/forms";
import { formSubmissionsApi } from "@/lib/api/form-submissions";
import { settingsApi } from "@/lib/api/settings";
import { collectionDataApi, type CollectionDataItem } from "@/lib/api/collection-data";
import type { ExtensionComponentProps } from "@/extensions/registry";
import type { Form, FormSubmission, Setting } from "@/types/entities";

type DentistOption = {
  id: number;
  name: string;
};

type BookingRow = FormSubmission & {
  dentist_id?: number;
  appointment_date?: string;
  appointment_time?: string;
  status?: string;
  full_name?: string;
  whatsapp?: string;
  notes?: string;
};

const formatFieldLabel = (key: string) => {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const READ_ONLY_BOOKING_KEYS = new Set(["id", "created_at", "updated_at"]);
const DEFAULT_BOOKING_FIELDS = [
  "appointment_date",
  "appointment_time",
  "dentist_id",
  "full_name",
  "whatsapp",
  "notes",
  "status",
];

const serializeFieldValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const toUpdateFieldValue = (key: string, rawValue: string, originalValue: unknown) => {
  if (key === "dentist_id" || typeof originalValue === "number") {
    const numericValue = Number(rawValue);
    return Number.isNaN(numericValue) ? rawValue : numericValue;
  }

  if (typeof originalValue === "boolean") {
    return rawValue === "true";
  }

  if (typeof originalValue === "object" && originalValue !== null) {
    try {
      return JSON.parse(rawValue);
    } catch {
      return rawValue;
    }
  }

  return rawValue;
};

const toTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeWhatsappNumber = (rawValue: string) => {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";

  const normalizedBase = trimmed.replace(/[\s()-]/g, "");

  if (normalizedBase.startsWith("+")) {
    const digits = normalizedBase.slice(1).replace(/\D/g, "");
    return digits ? `+${digits}` : "";
  }

  if (normalizedBase.startsWith("00")) {
    const digits = normalizedBase.slice(2).replace(/\D/g, "");
    return digits ? `+${digits}` : "";
  }

  const digits = normalizedBase.replace(/\D/g, "");
  if (!digits) return "";

  if (digits.startsWith("62")) {
    return `+${digits}`;
  }

  if (digits.startsWith("0")) {
    return `+62${digits.slice(1)}`;
  }

  return `+62${digits}`;
};

const toCreateFieldValue = (key: string, rawValue: string) => {
  if (key === "dentist_id") {
    const numericValue = Number(rawValue);
    return Number.isNaN(numericValue) ? rawValue : numericValue;
  }

  return rawValue;
};

const applyTemplateVariables = (template: string, row: BookingRow) => {
  const formatAppointmentDateTime = (appointmentDateRaw: unknown, appointmentTimeRaw: unknown) => {
    const appointmentDate = String(appointmentDateRaw ?? "").trim();
    const appointmentTime = String(appointmentTimeRaw ?? "").trim();

    if (!appointmentDate) {
      return "";
    }

    const [yearRaw, monthRaw, dayRaw] = appointmentDate.split("-");
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);

    if (!year || !month || !day) {
      return "";
    }

    let formattedTime = "00:00";
    if (appointmentTime) {
      const [startTimeRaw] = appointmentTime.split("-");
      const [hourRaw = "", minuteRaw = ""] = startTimeRaw.trim().split(":");
      const hour = Number(hourRaw);
      const minute = Number(minuteRaw);
      if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
        formattedTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      }
    }

    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year} ${formattedTime} UTC+7`;
  };

  const status = String(row.status ?? "").toLowerCase();
  const values: Record<string, string> = {
    fullName: String(row.full_name ?? ""),
    appointmentTime: String(row.appointment_time ?? ""),
    appointmentDate: formatAppointmentDateTime(row.appointment_date, row.appointment_time),
    whatsapp: String(row.whatsapp ?? ""),
    notes: String(row.notes ?? ""),
    email: String((row as Record<string, unknown>).email ?? ""),
    pending: String((row as Record<string, unknown>).pending ?? (status === "pending" ? "pending" : "")),
    serviceType: String(
      (row as Record<string, unknown>).service_type ??
      (row as Record<string, unknown>).serviceType ??
      ""
    ),
  };

  return template.replace(/\$(fullName|appointmentTime|appointmentDate|whatsapp|notes|email|pending|serviceType)\b/g, (_, key: string) => {
    return values[key] ?? "";
  });
};

const toTimeSortValue = (timeRange: string) => {
  const [start] = timeRange.split("-");
  if (!start) return Number.MAX_SAFE_INTEGER;
  const [hoursRaw, minutesRaw] = start.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return hours * 60 + minutes;
};

const getBookingForm = (forms: Form[]) => {
  const fallbackByEndpoint = forms.find((form) => form.endpoint.toLowerCase() === "bookings");
  if (fallbackByEndpoint) return fallbackByEndpoint;

  return forms.find((form) => form.name.toLowerCase() === "bookings") ?? null;
};

export function EscaladeDentalBookingManager({ tenantSlug, extension }: ExtensionComponentProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [createFormError, setCreateFormError] = useState<string | null>(null);
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [dentists, setDentists] = useState<DentistOption[]>([]);
  const [bookingFormName, setBookingFormName] = useState<string | null>(null);
  const [messageTemplates, setMessageTemplates] = useState<Record<string, string>>({});

  const [selectedDentistId, setSelectedDentistId] = useState<string>("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [hidePastDates, setHidePastDates] = useState(true);
  const [editingRow, setEditingRow] = useState<BookingRow | null>(null);
  const [editingDraft, setEditingDraft] = useState<Record<string, string>>({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState<Record<string, string>>({});
  const [deleteRow, setDeleteRow] = useState<BookingRow | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [forms, dentistItems, settings] = await Promise.all([
          formsApi.getAllForTenant(tenantSlug),
          collectionDataApi.getAll(tenantSlug, "dentists"),
          settingsApi.getAllForTenantBySlug(tenantSlug),
        ]);

        if (isCancelled) return;

        const bookingForm = getBookingForm(forms);
        if (!bookingForm) {
          setRows([]);
          setDentists([]);
          setBookingFormName(null);
          setError("Booking form not found. Expected endpoint from extension or fallback 'bookings'.");
          setIsLoading(false);
          return;
        }

        const submissions = await formSubmissionsApi.getAllForForm(tenantSlug, bookingForm.name);

        if (isCancelled) return;

        const dentistOptions = (dentistItems as CollectionDataItem[])
          .map((item) => ({
            id: Number(item.id),
            name: String(item.name ?? `Dentist #${item.id}`),
          }))
          .filter((item) => Number.isInteger(item.id) && item.id > 0)
          .sort((a, b) => a.name.localeCompare(b.name));

        setDentists(dentistOptions);
        setBookingFormName(bookingForm.name);
        setRows(submissions as BookingRow[]);
        const templateAttributes = new Set(["template_confirmed", "template_cancelled", "template_pending"]);
        const templateMap = (settings as Setting[]).reduce<Record<string, string>>((accumulator, setting) => {
          if (!templateAttributes.has(setting.attribute)) {
            return accumulator;
          }

          const value = String(setting.value ?? "").trim();
          if (!value) {
            return accumulator;
          }

          accumulator[setting.attribute] = value;
          return accumulator;
        }, {});

        setMessageTemplates(templateMap);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Failed to load booking records");
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [extension.endpoint_path, tenantSlug]);

  const handleOpenUpdate = (row: BookingRow) => {
    const nextDraft: Record<string, string> = {};
    for (const [key, value] of Object.entries(row)) {
      if (READ_ONLY_BOOKING_KEYS.has(key)) {
        continue;
      }

      nextDraft[key] = serializeFieldValue(value);
    }

    setEditingRow(row);
    setEditingDraft(nextDraft);
    setEditFormError(null);
  };

  const handleUpdateBooking = async () => {
    if (!editingRow || !bookingFormName) {
      setEditFormError("Booking form is not available.");
      return;
    }

    setIsSaving(true);
    setEditFormError(null);
    try {
      const payload = Object.entries(editingDraft).reduce<Record<string, unknown>>((accumulator, [key, rawValue]) => {
        const originalValue = (editingRow as Record<string, unknown>)[key];
        accumulator[key] = toUpdateFieldValue(key, rawValue, originalValue);
        return accumulator;
      }, {});

      const updated = await formSubmissionsApi.update(tenantSlug, bookingFormName, editingRow.id, payload);

      setRows((previousRows) => {
        return previousRows.map((row) => {
          if (row.id !== editingRow.id) {
            return row;
          }
          return updated as BookingRow;
        });
      });
      setEditingRow(null);
      setEditingDraft({});
    } catch (updateError) {
      setEditFormError(updateError instanceof Error ? updateError.message : "Failed to update booking request");
    } finally {
      setIsSaving(false);
    }
  };

  const editableFieldKeys = useMemo(() => {
    const keys = new Set(DEFAULT_BOOKING_FIELDS);

    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (READ_ONLY_BOOKING_KEYS.has(key)) {
          continue;
        }

        keys.add(key);
      }
    }

    return Array.from(keys);
  }, [rows]);

  const openCreateDialog = () => {
    const nextDraft = editableFieldKeys.reduce<Record<string, string>>((accumulator, key) => {
      accumulator[key] = key === "status" ? "pending" : "";
      return accumulator;
    }, {});

    setCreatingDraft(nextDraft);
    setCreateFormError(null);
    setIsCreateDialogOpen(true);
  };

  const handleCreateBooking = async () => {
    if (!bookingFormName) {
      setCreateFormError("Booking form is not available.");
      return;
    }

    setIsSaving(true);
    setCreateFormError(null);
    try {
      const payload = Object.entries(creatingDraft).reduce<Record<string, unknown>>((accumulator, [key, rawValue]) => {
        if (rawValue.trim() === "") {
          return accumulator;
        }

        accumulator[key] = toCreateFieldValue(key, rawValue);
        return accumulator;
      }, {});

      const created = await formSubmissionsApi.create(tenantSlug, bookingFormName, payload);
      setRows((previousRows) => [created as BookingRow, ...previousRows]);
      setIsCreateDialogOpen(false);
      setCreatingDraft({});
    } catch (createError) {
      setCreateFormError(createError instanceof Error ? createError.message : "Failed to create booking request");
    } finally {
      setIsSaving(false);
    }
  };

  const getWhatsappMessage = (row: BookingRow) => {
    const status = String(row.status ?? "pending").toLowerCase();

    if (status === "confirmed") {
      return applyTemplateVariables(messageTemplates.template_confirmed || "", row);
    }

    if (status === "cancelled") {
      return applyTemplateVariables(messageTemplates.template_cancelled || "", row);
    }

    if (status === "pending") {
      return applyTemplateVariables(messageTemplates.template_pending || "", row);
    }

    return "";
  };

  const openWhatsappChat = (row: BookingRow) => {
    const whatsapp = row.whatsapp;
    const normalized = normalizeWhatsappNumber(String(whatsapp ?? ""));
    if (!normalized) {
      setError("WhatsApp number is missing or invalid.");
      return;
    }

    const waNumber = normalized.replace(/^\+/, "");
    const message = getWhatsappMessage(row).trim();
    const messageQuery = message ? `?text=${encodeURIComponent(message)}` : "";
    window.open(`https://wa.me/${waNumber}${messageQuery}`, "_blank", "noopener,noreferrer");
  };

  const handleDeleteBooking = async () => {
    if (!deleteRow || !bookingFormName) {
      setError("Booking form is not available.");
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await formSubmissionsApi.delete(tenantSlug, bookingFormName, deleteRow.id);
      setRows((previousRows) => previousRows.filter((row) => row.id !== deleteRow.id));
      setDeleteRow(null);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Failed to delete booking request");
    } finally {
      setIsSaving(false);
    }
  };

  const dentistNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const dentist of dentists) {
      map.set(dentist.id, dentist.name);
    }
    return map;
  }, [dentists]);

  const filteredRows = useMemo(() => {
    const today = toTodayIsoDate();
    const nextRows = rows.filter((row) => {
      const dentistId = Number(row.dentist_id);
      const appointmentDate = typeof row.appointment_date === "string" ? row.appointment_date : "";

      if (!appointmentDate) {
        return false;
      }

      if (hidePastDates && appointmentDate < today) {
        return false;
      }

      if (selectedDate && appointmentDate !== selectedDate) {
        return false;
      }

      if (selectedDentistId !== "all" && dentistId !== Number(selectedDentistId)) {
        return false;
      }

      return true;
    });

    nextRows.sort((a, b) => {
      const aDate = String(a.appointment_date ?? "");
      const bDate = String(b.appointment_date ?? "");
      const dateSort = aDate.localeCompare(bDate);
      if (dateSort !== 0) return dateSort;

      const aTime = String(a.appointment_time ?? "");
      const bTime = String(b.appointment_time ?? "");
      return toTimeSortValue(aTime) - toTimeSortValue(bTime);
    });

    return nextRows;
  }, [hidePastDates, rows, selectedDate, selectedDentistId]);

  return (
    <>
      <Card className="p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="booking-filter-dentist">Dentist</Label>
              <select
                id="booking-filter-dentist"
                value={selectedDentistId}
                onChange={(event) => setSelectedDentistId(event.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All</option>
                {dentists.map((dentist) => (
                  <option key={dentist.id} value={String(dentist.id)}>
                    {dentist.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="booking-filter-date">Date</Label>
              <input
                id="booking-filter-date"
                type="date"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>

            <div className="flex items-center self-center gap-2 pb-1">
              <Switch
                id="booking-filter-hide-past"
                checked={hidePastDates}
                onCheckedChange={setHidePastDates}
              />
              <Label htmlFor="booking-filter-hide-past">Hide past dates</Label>
            </div>

            <Button
              type="button"
              className="ml-auto"
              onClick={openCreateDialog}
              disabled={isSaving || !bookingFormName}
            >
              <PlusIcon className="mr-2 size-4" />
              Add Booking
            </Button>
          </div>

          <Alert variant="info">
            <CircleAlertIcon className="h-4 w-4" />
            <AlertTitle>Template messages</AlertTitle>
            <AlertDescription>
              WhatsApp message templates are managed in the Settings section using `template_confirmed`,
              `template_cancelled`, and `template_pending`.
            </AlertDescription>
          </Alert>

          {error ? <div className="text-sm text-destructive">{error}</div> : null}
          {isLoading ? <div className="text-sm text-muted-foreground">Loading booking submissions...</div> : null}

          {!isLoading && !error ? (
            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Time</th>
                    <th className="px-3 py-2 font-medium">Dentist</th>
                    <th className="px-3 py-2 font-medium">Patient</th>
                    <th className="px-3 py-2 font-medium">WhatsApp</th>
                    <th className="px-3 py-2 font-medium">Notes</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">
                        No bookings found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => {
                      const dentistId = Number(row.dentist_id);
                      const status = String(row.status ?? "pending").toLowerCase();
                      const statusVariant = status === "confirmed"
                        ? "default"
                        : status === "pending"
                          ? "secondary"
                          : "outline";

                      return (
                        <tr key={row.id} className="border-t">
                          <td className="px-3 py-2">{String(row.appointment_date ?? "-")}</td>
                          <td className="px-3 py-2">{String(row.appointment_time ?? "-")}</td>
                          <td className="px-3 py-2">{dentistNameMap.get(dentistId) ?? `Dentist #${dentistId}`}</td>
                          <td className="px-3 py-2">{String(row.full_name ?? "-")}</td>
                          <td className="px-3 py-2">{String(row.whatsapp ?? "-")}</td>
                          <td className="px-3 py-2 max-w-[260px] whitespace-pre-wrap break-words">{String(row.notes ?? "-")}</td>
                          <td className="px-3 py-2">
                            <Badge variant={statusVariant}>{status}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-green-600 hover:text-green-600"
                                onClick={() => openWhatsappChat(row)}
                                disabled={isSaving}
                                aria-label={`Send WhatsApp message to booking #${row.id}`}
                                title="Send WhatsApp message"
                              >
                                <MessageCircleIcon className="size-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleOpenUpdate(row)}
                                disabled={isSaving || !bookingFormName}
                                aria-label={`Update booking #${row.id}`}
                                title="Update booking"
                              >
                                <EditIcon className="size-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => setDeleteRow(row)}
                                disabled={isSaving || !bookingFormName}
                                aria-label={`Delete booking #${row.id}`}
                                title="Delete booking"
                              >
                                <TrashIcon className="size-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
      </Card>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            setCreatingDraft({});
            setCreateFormError(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Booking Request</DialogTitle>
            <DialogDescription>Create a new booking in the bookings form.</DialogDescription>
          </DialogHeader>
          {createFormError ? <div className="text-sm text-destructive">{createFormError}</div> : null}
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {editableFieldKeys.map((key) => {
              const fieldId = `booking-create-${key}`;
              const value = creatingDraft[key] ?? "";
              const isStatusField = key === "status";
              const isNotesField = key === "notes";
              const isDateField = key.endsWith("_date");
              const isEmailField = key.includes("email");
              const isPhoneField = key.includes("whatsapp") || key.includes("phone");
              const isNumericField = key === "dentist_id";

              return (
                <div key={key} className="space-y-1">
                  <Label htmlFor={fieldId}>{formatFieldLabel(key)}</Label>
                  {isStatusField ? (
                    <select
                      id={fieldId}
                      value={value}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setCreatingDraft((previous) => ({ ...previous, [key]: nextValue }));
                      }}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  ) : isNotesField ? (
                    <textarea
                      id={fieldId}
                      value={value}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setCreatingDraft((previous) => ({ ...previous, [key]: nextValue }));
                      }}
                      rows={4}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  ) : (
                    <input
                      id={fieldId}
                      type={isDateField ? "date" : isEmailField ? "email" : isPhoneField ? "tel" : isNumericField ? "number" : "text"}
                      value={value}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setCreatingDraft((previous) => ({ ...previous, [key]: nextValue }));
                      }}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setCreatingDraft({});
                setCreateFormError(null);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleCreateBooking()} disabled={isSaving || !bookingFormName}>
              Add Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editingRow)} onOpenChange={(open) => {
        if (!open) {
          setEditingRow(null);
          setEditingDraft({});
          setEditFormError(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update Booking Request</DialogTitle>
            <DialogDescription>
              Edit booking details for {editingRow?.full_name ? String(editingRow.full_name) : `Booking #${editingRow?.id ?? ""}`}
            </DialogDescription>
          </DialogHeader>
          {editFormError ? <div className="text-sm text-destructive">{editFormError}</div> : null}
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {editingRow
              ? Object.entries(editingRow)
                .filter(([key]) => !READ_ONLY_BOOKING_KEYS.has(key))
                .map(([key]) => {
                  const fieldId = `booking-edit-${key}`;
                  const value = editingDraft[key] ?? "";
                  const originalValue = (editingRow as Record<string, unknown>)[key];
                  const isStatusField = key === "status";
                  const isNotesField = key === "notes";
                  const isDateField = key.endsWith("_date");
                  const isEmailField = key.includes("email");
                  const isPhoneField = key.includes("whatsapp") || key.includes("phone");
                  const isNumericField = key === "dentist_id" || typeof originalValue === "number";

                  return (
                    <div key={key} className="space-y-1">
                      <Label htmlFor={fieldId}>{formatFieldLabel(key)}</Label>
                      {isStatusField ? (
                        <select
                          id={fieldId}
                          value={value}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setEditingDraft((previous) => ({ ...previous, [key]: nextValue }));
                          }}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      ) : isNotesField ? (
                        <textarea
                          id={fieldId}
                          value={value}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setEditingDraft((previous) => ({ ...previous, [key]: nextValue }));
                          }}
                          rows={4}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                      ) : (
                        <input
                          id={fieldId}
                          type={isDateField ? "date" : isEmailField ? "email" : isPhoneField ? "tel" : isNumericField ? "number" : "text"}
                          value={value}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setEditingDraft((previous) => ({ ...previous, [key]: nextValue }));
                          }}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        />
                      )}
                    </div>
                  );
                })
              : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditingRow(null);
                setEditingDraft({});
                setEditFormError(null);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleUpdateBooking()} disabled={isSaving || !bookingFormName}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(deleteRow)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteRow(null);
          }
        }}
        title="Delete Booking Request"
        description={
          deleteRow
            ? `Are you sure you want to delete booking request for ${String(deleteRow.full_name ?? `#${deleteRow.id}`)}?`
            : ""
        }
        onConfirm={handleDeleteBooking}
        confirmLabel="Delete"
        confirmVariant="destructive"
        confirmDisabled={isSaving || !bookingFormName}
        cancelDisabled={isSaving}
      />
    </>
  );
}
